<template>
  <div v-if="!privateMonetaryBoundaryEnabled">
    <div
      v-if="canShowPrice"
      :class="[
        'priceTag',
        'noWrap',
        isCurrentUserProductOwner ? 'graybadge' : buttonClass,
        'smallerClickableHeight',
        compact ? 'compact' : null,
      ]"
      @click="!isCurrentUserProductOwner && priceClicked()"
    >
      <div v-if="isLoading" class="loading-message">Loading...</div>
      <div class="row alignCentreRow justifyToCentre" v-else>
        <span class="priceNumberSymbol">
          {{ displayConvertedPrice }} {{ buyercoinSymbol }}
        </span>
        <!-- <small v-if="isProductOwner" class="gray">Yours</small> -->
        <slot></slot>
      </div>
    </div>

    <div v-else>
      <div v-if="!isOpenEconomy">
        <small class="gray">Closed economy</small>
      </div>
      <div v-else>
        <svg class="verySmall" v-haptic>
          <use href="@/assets/icons/iconset.svg#cat"></use>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, watch } from "vue";
import { useStore } from "vuex";
import { calculatePrice } from "@/utils/exchangeRateCalculator.js";
import { formatNumberTo4Digits } from "@/utils/numberFormattingTo4digits";
import { useClapActorContext } from "@/composables/useClapSession";
import {
  assertLegacyMonetaryClientAvailable,
  isPrivateMonetaryBoundaryEnabled as privateMonetaryBoundaryIsEnabled,
} from "@/services/legacyMonetaryBoundary";

export default {
  name: "CentTranslatedPrice",
  props: {
    product: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    isProductOwner: Boolean,
    buttonClass: {
      type: String,
      default: "priceTagAction",
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const { currentUserUID, selectedOrgUID, currentWallet } =
      useClapActorContext();
    const privateMonetaryBoundaryEnabled =
      privateMonetaryBoundaryIsEnabled();
    const isLoading = computed(() => false);
    const isOpenEconomy = computed(
      () =>
        props.product.organisationAllowsTrades !== false &&
        props.product.allowTrades !== false,
    );

    const isCurrentUserProductOwner = computed(() => {
      return currentUserUID.value === props.product.owner;
    });

    const isUserInSameOrganisation = computed(() => {
      return selectedOrgUID.value === props.product.organisation;
    });

    const canShowPrice = computed(() => {
      return (
        isCurrentUserProductOwner.value ||
        isUserInSameOrganisation.value ||
        isOpenEconomy.value
      );
    });

    const priceClicked = () => {
      assertLegacyMonetaryClientAvailable();
      if (!canShowPrice.value || isCurrentUserProductOwner.value) return;

      emit("price-clicked", {
        product: props.product,
        productPrice: props.product.price, // Original price in seller's currency
        convertedPrice: convertedPrice.value, // Price converted to buyer's currency
        buyerCoinSymbol: buyercoinSymbol,
        sellerCoinSymbol: props.product.coinSymbol,
        type: props.product.type,
      });
    };

    const sellerOrganisationExistsAndHasMembers = computed(() => {
      const orgUID = props.product.organisation;
      const orgDetails = store.getters.getSellerOrgDetails(orgUID) || {};

      if (!orgDetails) {
        return false;
      }

      return orgDetails.membersCount > 0 && orgDetails.totalOrgCoins > 0;
    });

    const userUnits_customOrgs = computed(() => store.getters.userUnitsData || {});
    const userHasOrganisations = computed(
      () => Object.keys(userUnits_customOrgs.value).length > 0,
    );

    const buyerOrgDetails = computed(
      () => store.getters.getBuyerOrgDetails(selectedOrgUID.value) || {},
    );

    const buyercoinSymbol = computed(
      () =>
        currentWallet.value?.monetary_parameters?.coin_symbol ||
        store.getters.coinSymbol ||
        "Ʉ",
    );

    watch(buyercoinSymbol, (newSymbol) => {
      emit("buyercoinSymbolChanged", newSymbol);
    });

    watch(userHasOrganisations, (hasOrgs) => {
      if (!hasOrgs) {
        emit("userHasNoOrganisations");
      }
    });

    watch(buyerOrgDetails, (details) => {
      if (details.totalOrgCoins === 0) {
        emit("noCoinsInBuyerOrg");
      } else {
        emit("hasCoinsInBuyerOrg");
      }
    });

    const convertedPrice = computed(() => {
      if (selectedOrgUID.value === props.product.organisation) {
        return props.product.price;
      } else {
        const sellerOrgDetails =
          store.getters.getSellerOrgDetails(props.product.organisation) || {};
        const buyerMembersCount = buyerOrgDetails.value?.membersCount || 1;
        const buyerTotalCoins = buyerOrgDetails.value?.totalOrgCoins || 0;
        const sellerMembersCount = sellerOrgDetails.membersCount || 1;
        const sellerTotalCoins = sellerOrgDetails.totalOrgCoins || 0;

        // Validate that we have valid data before calculating
        if (buyerTotalCoins <= 0 || sellerTotalCoins <= 0) {
          console.warn("[CentTranslatedPrice] Invalid coin data:", {
            buyerTotalCoins,
            sellerTotalCoins,
            productId: props.product.id,
          });
          return 0; // Return 0 instead of calling calculatePrice with invalid data
        }

        return calculatePrice(
          buyerMembersCount,
          sellerMembersCount,
          props.product.price,
          buyerTotalCoins,
          sellerTotalCoins,
        );
      }
    });

    const displayConvertedPrice = computed(() => {
      return isFinite(convertedPrice.value)
        ? formatNumberTo4Digits(convertedPrice.value)
        : "Processing...";
    });

    return {
      isLoading,
      convertedPrice,
      displayConvertedPrice,
      buyercoinSymbol,
      buyerOrgDetails,
      userHasOrganisations,
      sellerOrganisationExistsAndHasMembers,
      priceClicked,
      userUnits_customOrgs,
      isOpenEconomy,
      isCurrentUserProductOwner,
      canShowPrice,
      privateMonetaryBoundaryEnabled,
    };
  },
};
</script>

<style scoped>
.priceTag {
  display: flex;
  align-items: center;
}

.priceTag .row {
  gap: var(--space-3) !important;
}

.priceTag.compact {
  padding: 0 var(--space-3);
}

.priceTag.compact .row {
  gap: var(--space-2) !important;
}

.priceTag.priceTagAction {
  background-color: var(--color-surface-canvas);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-surface-raised);
  cursor: pointer;
  padding: 0 var(--space-4);
  transition: transform 0.15s ease;
  width: fit-content;
}

.priceTag.priceTagAction:hover {
  transform: translateY(var(--motion-pressed-offset));
}

.priceNumberSymbol {
  font-weight: var(--font-weight-bold);
}
</style>
